import React, { useCallback } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';

import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService from '@services/BrandingsService';
import { MAP_LAYOUT_VERSION } from '@interfaces/map';

import { useEditorMode } from '../EditorModeContext';
import { useMapEditor } from '../MapEditorContext';
import { useActiveBrandingId } from '../useActiveBrandingId';
import { useCanUseMapEditor } from '../useCanUseMapEditor';
import {
  findCatalogElement,
  hasBuiltGeometry,
  isGeometryMisconfigured,
} from '../mapEditorCatalog';
import { useMapEditorCatalog } from '../useMapEditorCatalog';
import { findRefusalAt, spawnLayoutInUnreal } from '../mapEditorUnreal';
import { useInspectorStyles } from './styles';

const MapEditorInspector = () => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const {
    chain,
    removeRoomAt,
    moveRoom,
    clearChain,
    markPublished,
    isDirty,
    refusals,
    revertDraft,
    canRevert,
  } = useMapEditor();
  const { sendMessageToUnreal } = useMytaverse();
  const currentBrandingId = useActiveBrandingId();
  const { setAutosaveStatus } = useEditorMode();
  const { elements: catalogElements } = useMapEditorCatalog();

  // Gated on the map having a room kit at all, not just on the feature flag — see
  // useCanUseMapEditor. This panel does not render on a map without one, so the check is
  // here for the Save button rather than for the panel.
  const canSpawnLayout = useCanUseMapEditor();

  // The only thing in this panel that reaches the other participants. Everything else —
  // adding, removing, reordering, clearing — stays in this author's draft, which Unreal
  // renders locally as a preview, until they press it.
  //
  // Unreal is told first and the branding write follows. The spawn is what the author is
  // watching for, and holding it behind a round-trip would make Save feel broken on a slow
  // connection; a failed write costs the layout its persistence, not its publish.
  const handleSave = useCallback(() => {
    markPublished(chain);
    // The catalog rides along so the authority does not have to resolve the ids itself — see
    // describeRoom in mapEditorUnreal.
    spawnLayoutInUnreal(sendMessageToUnreal, chain, catalogElements);

    if (!currentBrandingId) {
      return;
    }

    // Saved as the author's intent, not as what Unreal ended up building — those differ
    // whenever a room is refused for overlapping, and persisting the built list would
    // silently rewrite the layout the author authored.
    setAutosaveStatus('saving');
    BrandingsService.updateBranding(currentBrandingId, {
      layout: { version: MAP_LAYOUT_VERSION, rooms: chain },
    })
      .then(() => setAutosaveStatus('saved'))
      .catch((saveError: Error) => {
        console.error(
          `Failed to save the map layout: ${saveError?.message}`,
          saveError,
        );
        setAutosaveStatus('error');
      });
  }, [
    sendMessageToUnreal,
    chain,
    markPublished,
    currentBrandingId,
    setAutosaveStatus,
    catalogElements,
  ]);

  // Empties the draft only. Saving an empty draft is what tears the layout down for
  // everyone — SPAWN_LAYOUT carries clearExisting, so "nothing" publishes as a clear
  // and CLEAR_LAYOUT is not needed as a separate step.
  const handleClear = useCallback(() => {
    clearChain();
  }, [clearChain]);

  return (
    <Box>
      {chain.length === 0 ? (
        <Typography sx={styles.emptyHint}>
          {translate(
            'editorMode.mapEditorEmptyChain',
            'No rooms yet. Pick elements from the catalog on the left to build the floor plan.',
          )}
        </Typography>
      ) : (
        chain.map((id, index) => {
          const element = findCatalogElement(catalogElements, id);
          const name = element?.displayName ?? id;
          const isMissingGeometry = !!element && !hasBuiltGeometry(element);
          const isMisconfigured = !!element && isGeometryMisconfigured(element);

          // Only read while the draft still matches what was published. Refusals are keyed by
          // position in the chain that was sent, so the author's first edit shifts every row out
          // from under them — showing a stale report beats nothing, but pointing it at the wrong
          // room does not.
          const refusal = isDirty ? undefined : findRefusalAt(refusals, index);

          return (
            <Box key={`${id}-${index}`} sx={styles.mapEditorRow}>
              <Typography sx={styles.mapEditorRowIndex}>
                {String(index + 1).padStart(2, '0')}
              </Typography>
              <Box sx={styles.compactInfo}>
                <Typography sx={styles.compactTitle}>{name}</Typography>
                <Typography sx={styles.compactSubtitle}>{id}</Typography>
              </Box>
              {/* A refusal outranks the catalog markers below: it reports what the authority
                  actually did with this row, where those only describe what the element declares.
                  A refused room is not standing at all, which is the more important thing to say. */}
              {refusal && (
                <Tooltip
                  title={
                    refusal.reason === 'overlap'
                      ? translate(
                          'editorMode.mapEditorRefusedOverlap',
                          'Unreal refused this room — it lands on top of one already standing. Move or remove it, then Save again.',
                        )
                      : translate(
                          'editorMode.mapEditorRefusedTruncated',
                          'Never attempted — the chain stopped at the refused room above it.',
                        )
                  }
                  placement="left"
                  enterDelay={300}
                >
                  <BlockOutlinedIcon
                    sx={{
                      fontSize: 14,
                      // Amber for a truncated room: nothing is wrong with it, it simply never got
                      // its turn, and it goes away on its own once the overlap above is resolved.
                      color:
                        refusal.reason === 'overlap' ? '#FF6B6B' : '#FFB800',
                      flexShrink: 0,
                    }}
                    data-testid={`map-editor-refused-${index}`}
                  />
                </Tooltip>
              )}
              {!refusal && isMissingGeometry && (
                <Tooltip
                  title={
                    isMisconfigured
                      ? translate(
                          'editorMode.mapEditorGeometryUnavailable',
                          'This element names a room this build cannot load — Unreal falls back to a grey placeholder',
                        )
                      : translate(
                          'editorMode.mapEditorNoGeometry',
                          'This element has no room built yet — Unreal builds a grey placeholder sized to it',
                        )
                  }
                  placement="left"
                  enterDelay={300}
                >
                  {/* Red rather than amber for the misconfigured case: an unbuilt room is a
                      normal state an author can ignore, a room the build lost is not. */}
                  <WarningAmberOutlinedIcon
                    sx={{
                      fontSize: 14,
                      color: isMisconfigured ? '#FF6B6B' : '#FFB800',
                      flexShrink: 0,
                    }}
                    data-testid={
                      isMisconfigured
                        ? `map-editor-unavailable-${id}`
                        : `map-editor-placeholder-${id}`
                    }
                  />
                </Tooltip>
              )}
              <Box sx={styles.compactActions}>
                <IconButton
                  size="small"
                  sx={styles.iconSquareButton}
                  onClick={() => moveRoom(index, -1)}
                  disabled={index === 0}
                  aria-label={translate(
                    'editorMode.mapEditorMoveUp',
                    'Move up',
                  )}
                  data-testid={`map-editor-move-up-${index}`}
                >
                  <ArrowUpwardOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={styles.iconSquareButton}
                  onClick={() => moveRoom(index, 1)}
                  disabled={index === chain.length - 1}
                  aria-label={translate(
                    'editorMode.mapEditorMoveDown',
                    'Move down',
                  )}
                  data-testid={`map-editor-move-down-${index}`}
                >
                  <ArrowDownwardOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={[styles.iconSquareButton, styles.iconDangerButton]}
                  onClick={() => removeRoomAt(index)}
                  aria-label={translate('editorMode.mapEditorRemove', 'Remove')}
                  data-testid={`map-editor-remove-${index}`}
                >
                  <DeleteOutlineOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>
            </Box>
          );
        })
      )}

      {/* No layout-level warning about unbuilt elements. A greybox is the designed stand-in, not a
          fault: it keeps a freshly-seeded catalog walkable before any art exists. Counting them as
          problems made an intentional placeholder read as a spawn failure. The per-row marker below
          still says which specific element is a placeholder, which is the part an author acts on. */}

      {/* States plainly that the draft has not left this browser. Without it an author
          has no way to tell whether the room they just added is visible to everyone. */}
      {isDirty && (
        <Box sx={styles.mapEditorUnsavedBanner}>
          <EditNoteOutlinedIcon sx={{ fontSize: 14, color: '#AEB6FF' }} />
          <Typography sx={styles.mapEditorUnsavedText}>
            {/* An emptied draft needs its own wording. "Only you can see these changes" is true but
                unhelpful when the change is *nothing* and the rooms are still standing in front of
                you — Clear empties the draft, and it takes a Save to publish that as a teardown. */}
            {chain.length === 0
              ? translate(
                  'editorMode.mapEditorUnsavedEmpty',
                  'Draft is empty. Any rooms still standing are published — Save to remove them for everyone.',
                )
              : translate(
                  'editorMode.mapEditorUnsaved',
                  'Unsaved — only you can see these changes. Save to apply them for everyone.',
                )}
          </Typography>
        </Box>
      )}

      <Box sx={styles.mapEditorFooter}>
        {/* Save is not gated on a non-empty chain: an empty draft is a valid layout —
            "nothing" — and saving it is how a standing layout gets torn down. Gating it
            would leave the author with no way to remove what they published. It is
            gated on isDirty, since re-sending an unchanged layout would make every
            other participant's rooms despawn and respawn for no reason. */}
        <Button
          variant="contained"
          size="small"
          sx={styles.mapEditorSaveButton}
          onClick={handleSave}
          disabled={!canSpawnLayout || !isDirty}
          data-testid="map-editor-save-btn"
        >
          {translate('editorMode.mapEditorSave', 'Save')}
        </Button>
        {/* The way back out of Clear. Enabled on any draft that differs from what is standing, not
            only on an emptied one — discarding a half-built draft is the same request. */}
        <Tooltip
          title={translate(
            'editorMode.mapEditorRevertHint',
            'Discard this draft and go back to the rooms that are standing',
          )}
          placement="top"
          enterDelay={300}
        >
          <Box component="span" sx={styles.mapEditorFooterAction}>
            <Button
              variant="outlined"
              size="small"
              sx={styles.spotActionButton}
              onClick={revertDraft}
              disabled={!canRevert}
              data-testid="map-editor-revert-btn"
            >
              {translate('editorMode.mapEditorRevert', 'Revert')}
            </Button>
          </Box>
        </Tooltip>
        <Button
          variant="outlined"
          size="small"
          sx={styles.spotActionButton}
          onClick={handleClear}
          disabled={!chain.length}
          data-testid="map-editor-clear-btn"
        >
          {translate('editorMode.mapEditorClear', 'Clear')}
        </Button>
      </Box>

      {!canSpawnLayout && (
        <Typography sx={styles.mapEditorNote}>
          {translate(
            'editorMode.mapEditorFlagDisabled',
            'Layout building is turned off for this map.',
          )}
        </Typography>
      )}
    </Box>
  );
};

export default MapEditorInspector;
